'use client';

import React, { FC, useEffect, useState } from 'react';

import useAuth from '@/context/AuthContext';

import { TypeGlobalNavFields } from '@/core/generated-types';

import { buildLinkUrl } from '@/utils/linkBuilder';

import { Link } from '@/navigation';

import { Translations } from '@/utils/translations/generateTranslationObject';

import { useDebouncedHover } from '../../hooks/useDebouncedHover';
import { useUserMenuLinks } from '../../hooks/useUserMenuLinks';

import { UserIcon } from '../../UserIcon/UserIcon';
import {
  CLICKED_ITEM_TYPES,
  CTA_NAMES,
  NAV_EVENT_TYPES,
  PAGE_LOCATIONS,
  trackAsyncItemClickedEvent,
  trackElementPresented,
  trackItemClickedEvent,
} from '@/utils/rudderstack';
import LineAndBlock from '@/components/checkout/Skeletons/LineAndBlock';
import useLocale from '@/hooks/useLocale';

export interface UserMenuProps {
  signInLink: TypeGlobalNavFields['signInLink'];
  linkMenuLoggedIn: TypeGlobalNavFields['linkMenuLoggedIn'];
  translations: Translations;
  hasButtonCta: boolean;
}

export const UserMenu: FC<UserMenuProps> = ({
  signInLink,
  linkMenuLoggedIn,
  translations,
  hasButtonCta,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const locale = useLocale();

  const { hardcodedLinks, isLoading } = useUserMenuLinks(translations);
  const { user } = useAuth();

  const { handleMouseEnter, handleMouseLeave } = useDebouncedHover({
    onShow: () => setIsDropdownOpen(true),
    onHide: () => setIsDropdownOpen(false),
  });

  useEffect(() => {
    if (isDropdownOpen) {
      trackElementPresented({
        name: NAV_EVENT_TYPES.PRIMARY_NAV_EXPANDED,
        title: 'User Menu',
      });
    }
  }, [isDropdownOpen]);

  // Unauthenticated user
  if (!user) {
    const signInHref = buildLinkUrl(signInLink, locale);

    return (
      <Link
        className="hover:text-blue-deep hover:underline"
        href={signInHref}
        onClick={async (e) => {
          e.preventDefault();

          await trackAsyncItemClickedEvent({
            url: window.location.href,
            type: CLICKED_ITEM_TYPES.IMAGE,
            pageLocation: PAGE_LOCATIONS.NAVIGATION,
            cta: CTA_NAMES.USER_ICON,
          });

          window.location.href = signInHref;
        }}
      >
        <span>{translations.signIn || 'Sign In'}</span>
      </Link>
    );
  }

  // Authenticated user
  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* User menu trigger */}
      <div className="flex items-center cursor-pointer gap-2 hover:text-blue-deep max-w-[200px]">
        {!hasButtonCta && <UserIcon />}

        <span
          className="max-w-28 text-base hover:underline whitespace-nowrap truncate"
          title={`${translations.hi || 'Hi'}, ${user.attributes?.name || 'User'}`}
        >
          {translations.hi || 'Hi'}, {user.attributes?.name || 'User'}
        </span>
      </div>

      {/* Dropdown menu */}
      {isDropdownOpen && (
        <div className="absolute mt-[32px] right-0 text-sm top-full bg-white border border-gray-200 rounded-md shadow-lg z-50 p-4 flex flex-col gap-2">
          {isLoading ? (
            <div className="w-28">
              <LineAndBlock numberOfLines={4} lineClassName="!h-4" />
            </div>
          ) : (
            <>
              {/* Contentful links after hardcoded links */}
              {linkMenuLoggedIn?.map((link) => {
                const href = buildLinkUrl(link, locale);

                return (
                  <Link
                    key={link.sys.id}
                    href={href}
                    className="block whitespace-nowrap hover:text-blue-deep hover:underline"
                    onClick={() => {
                      trackItemClickedEvent({
                        type: CLICKED_ITEM_TYPES.LINK,
                        cta: link.fields.linkText || '',
                        pageLocation: PAGE_LOCATIONS.NAVIGATION,
                        url: window.location.href,
                      });

                      handleMouseLeave();
                    }}
                  >
                    {link.fields.linkText}
                  </Link>
                );
              })}

              {/* Hardcoded links first */}
              {hardcodedLinks?.map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  onClick={() => {
                    trackItemClickedEvent({
                      type: CLICKED_ITEM_TYPES.LINK,
                      cta: link.label,
                      pageLocation: PAGE_LOCATIONS.NAVIGATION,
                      url: window.location.href,
                    });

                    handleMouseLeave();
                    link.onClick?.();
                  }}
                  className="block whitespace-nowrap hover:text-blue-deep hover:underline"
                >
                  {link.label}
                </a>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(UserMenu);
